<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <app-bar
      title="Betalen"
      :show-back-button="true"
      :show-profile-button="true"
    />

    <!-- Progress indicator -->
    <div class="bg-white border-b border-gray-100">
      <div class="max-w-2xl mx-auto px-5 py-3">
        <div class="flex items-center gap-2">
          <div class="flex items-center gap-1.5">
            <div
              class="w-6 h-6 rounded-full flex items-center justify-center text-xs"
              :class="
                step === 'bank'
                  ? 'bg-green-100 text-green-600'
                  : 'bg-green-600 text-white'
              "
            >
              <svg
                v-if="step === 'bank'"
                class="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span v-else>1</span>
            </div>
            <span
              class="text-xs"
              :class="step === 'method' ? 'text-gray-900' : 'text-gray-400'"
            >
              Methode
            </span>
          </div>
          <div class="flex-1 h-px bg-gray-200 mx-1" />
          <div class="flex items-center gap-1.5">
            <div
              class="w-6 h-6 rounded-full flex items-center justify-center text-xs"
              :class="
                step === 'bank'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-400'
              "
            >
              2
            </div>
            <span
              class="text-xs"
              :class="step === 'bank' ? 'text-gray-900' : 'text-gray-400'"
            >
              {{ selectedMethod === 'ideal' ? 'Bank' : 'Betalen' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <spinner-loader v-if="loading" />

    <template v-if="error">
      <div class="flex-1 flex items-center justify-center px-6">
        <div class="text-center">
          <h1 class="text-lg text-gray-900 mb-2">Oeps</h1>
          <p class="text-gray-500 text-sm">Er gaat iets fout...</p>
        </div>
      </div>
    </template>

    <template v-else>
      <!-- Content -->
      <div class="flex-1 overflow-y-auto">
        <div class="max-w-2xl mx-auto px-5 py-5">
          <!-- Order summary card -->
          <div class="bg-white rounded-2xl border border-gray-100 p-4 mb-5">
            <div class="flex items-center gap-3 mb-3">
              <div
                class="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center"
              >
                <img
                  v-if="eventDate.imageUrl"
                  :src="eventDate.imageUrl"
                  class="w-11 h-11 rounded-xl object-cover"
                />
                <span v-else class="text-xl">&#9917;</span>
              </div>
              <div class="min-w-0 flex-1">
                <h3 class="text-gray-900 text-sm truncate font-medium">
                  {{ eventDate.name }}
                </h3>
                <span class="text-gray-400 text-xs">{{ eventDate.date }}</span>
              </div>
            </div>

            <div class="border-t border-gray-100 pt-3 flex flex-col gap-1.5">
              <div class="flex items-center justify-between">
                <span class="text-gray-500 text-xs">
                  <template v-if="isPurchasingCreditcard">
                    {{ (activeCard && activeCard.name) || 'Strippenkaart' }}
                  </template>
                  <template v-else>
                    {{
                      dateCount === 1
                        ? '1 activiteit'
                        : `${dateCount} activiteiten`
                    }}
                  </template>
                </span>
                <span class="text-gray-700 text-xs">
                  {{
                    !isPurchasingCreditcard && dateCount > 1
                      ? `${dateCount}× `
                      : ''
                  }}{{ $n(pricePerActivity, 'currency') }}
                </span>
              </div>
              <div
                v-if="surcharge > 0"
                class="flex items-center justify-between"
              >
                <span class="text-gray-400 text-xs">Transactiekosten</span>
                <span class="text-gray-500 text-xs">
                  +{{ $n(surcharge, 'currency') }}
                </span>
              </div>
              <div
                class="flex items-center justify-between pt-2 mt-1 border-t border-dashed border-gray-100"
              >
                <span class="text-gray-700 text-sm">Totaal</span>
                <span class="text-gray-900 font-medium text-base">
                  {{ $n(grandTotal, 'currency') }}
                </span>
              </div>
            </div>
          </div>

          <!-- Payment method / bank selection -->
          <payment-options
            :step="step"
            :selected-method="selectedMethod"
            :selected-bank="selectedBank"
            :paypal-fee="paypalFeeValue"
            :banks="banks"
            @select-method="handleMethodSelect"
            @select-bank="handleBankSelect"
            @change-method="resetMethod"
          />

          <!-- Terms and conditions -->
          <div class="mt-5">
            <div class="flex items-start gap-3">
              <button
                type="button"
                class="mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                :class="
                  termsAccepted
                    ? 'bg-green-600 border-green-600'
                    : 'bg-white border-gray-300 hover:border-gray-400'
                "
                @click="termsAccepted = !termsAccepted"
              >
                <svg
                  v-if="termsAccepted"
                  class="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </button>
              <div class="text-xs leading-relaxed">
                <span class="text-gray-600">Ik ga akkoord met de&nbsp;</span>
                <router-link
                  :to="{
                    name: 'TermsAndConditions',
                    query: { hideTabBar: 'true' }
                  }"
                  target="_blank"
                  class="text-green-600 hover:text-green-700 underline underline-offset-2 transition-colors"
                >
                  algemene voorwaarden
                </router-link>
              </div>
            </div>

            <!-- Warning when terms not accepted -->
            <p
              v-if="!termsAccepted && canPay"
              class="mt-2 text-yellow-500 flex items-center gap-1.5 px-1 text-xs"
            >
              <svg
                class="w-3 h-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              Accepteer de voorwaarden om door te gaan met betalen
            </p>
          </div>

          <!-- Security note -->
          <div
            v-if="step === 'method'"
            class="mt-5 flex items-start gap-2.5 px-1"
          >
            <svg
              class="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <p class="text-gray-400 text-xs leading-relaxed">
              {{
                paymentType === PURCHASE_CREDITCARD
                  ? 'Je koopt een strippenkaart voor deze activiteit waarmee je je vervolgens direct kunt aanmelden.'
                  : 'Je betaalt alleen voor de losse activiteit(en). Veilig betalen via een versleutelde verbinding.'
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Sticky bottom CTA -->
      <div class="flex-shrink-0 bg-white border-t border-gray-100">
        <div class="max-w-2xl mx-auto px-5 pt-3 pb-4">
          <template
            v-if="
              step === 'method' && selectedMethod && selectedMethod !== 'ideal'
            "
          >
            <button
              :disabled="!termsAccepted || paymentLoading"
              class="w-full py-3.5 rounded-full transition-colors flex items-center justify-center gap-2"
              :class="
                termsAccepted && !paymentLoading
                  ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              "
              @click="startPayment"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <spinner-loader v-if="paymentLoading" class="inline-spinner" />
              <span v-else>Betaal {{ $n(grandTotal, 'currency') }}</span>
            </button>
          </template>
          <template v-else-if="step === 'bank' && selectedBank">
            <button
              :disabled="!termsAccepted || paymentLoading"
              class="w-full py-3.5 rounded-full transition-colors flex items-center justify-center gap-2"
              :class="
                termsAccepted && !paymentLoading
                  ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              "
              @click="startPayment"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <spinner-loader v-if="paymentLoading" class="inline-spinner" />
              <span v-else>
                Betaal {{ $n(grandTotal, 'currency') }} via
                {{ selectedBankName }}
              </span>
            </button>
          </template>
          <template v-else>
            <div class="text-center text-gray-400 py-1 text-sm">
              {{
                step === 'method'
                  ? 'Kies een betaalmethode om door te gaan'
                  : 'Kies je bank om door te gaan'
              }}
            </div>
          </template>
        </div>
      </div>

      <error-modal :show="showErrorModal" @close="showErrorModal = false" />
    </template>
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import SpinnerLoader from '@/components/SpinnerLoader'
import { mapGetters, mapState } from 'vuex'
import {
  EVENT_GET_CREDITCARDS,
  EVENT_UPDATE_ATTENDANCE
} from '@/store/actions/event'
import PaymentOptions from '@/components/PaymentOptions.vue'
import { OPTION_GET } from '@/store/actions/option'
import { PAYMENT_GET_METHODS } from '@/store/actions/payment'
import { CREDITCARD_PURCHASE } from '@/store/actions/creditcard'
import { PURCHASE_CREDITCARD, SINGLE_PAYMENT, IDEAL } from '@/utils/constants'
import ErrorModal from '@/components/ErrorModal'
import analytics from '@/utils/analytics'

export default {
  name: 'EventPayment',
  components: { AppBar, SpinnerLoader, PaymentOptions, ErrorModal },
  data() {
    return {
      dateId: this.$route.params.dateId,
      securityHash: this.$route.params.securityHash,
      error: false,
      PURCHASE_CREDITCARD: PURCHASE_CREDITCARD,
      SINGLE_PAYMENT: SINGLE_PAYMENT,
      paymentLoading: false,
      showErrorModal: false,
      step: 'method',
      selectedMethod: null,
      selectedBank: null,
      termsAccepted: false
    }
  },
  computed: {
    ...mapGetters(['authId']),
    ...mapState({
      loading: (state) => state.event.loading || state.date.loading,
      eventDate: (state) => state.date.eventDate,
      datesToUpdate: (state) => state.event.datesToUpdate,
      creditcards: (state) => state.event.creditcards,
      paypalFee: (state) => state.option.options.payments_paypal_fee,
      paymentMethods: (state) => state.payment.paymentMethods.data,
      participantCreditcards: (state) => state.participant.creditcards,
      creditcardAvailable: (state) => state.event.creditcards,
      paymentType: (state) => state.payment.paymentType
    }),
    activeCard: function () {
      return this.creditcards[this.eventDate.eventId]
        ? this.creditcards[this.eventDate.eventId][0]
        : null
    },
    banks: function () {
      return this.paymentMethods?.length
        ? this.paymentMethods.map((issuer) => ({
            key: issuer.bic,
            value: issuer.name,
            image: issuer.image
          }))
        : []
    },
    isPurchasingCreditcard: function () {
      return this.paymentType === PURCHASE_CREDITCARD
    },
    paypalFeeValue: function () {
      return this.isPurchasingCreditcard ? 0 : parseFloat(this.paypalFee) || 0
    },
    // Number of units being charged on this page. For a single-payment flow
    // that equals the number of pending date updates; when buying a
    // strippenkaart only one card is purchased regardless of how many dates
    // the user staged on the previous screen.
    dateCount: function () {
      if (this.isPurchasingCreditcard) return 1
      return this.datesToUpdate?.length || 1
    },
    // Per-unit price. For a creditcard purchase the "unit" is the card
    // itself, so we use its cost — not the event fee, which the backend
    // does not charge in this flow.
    pricePerActivity: function () {
      if (this.isPurchasingCreditcard) {
        return parseFloat(this.activeCard?.cost) || 0
      }
      return parseFloat(this.eventDate.costFeeIncluded) || 0
    },
    surcharge: function () {
      if (this.selectedMethod === 'paypal') return this.paypalFeeValue
      return 0
    },
    totalPrice: function () {
      return this.pricePerActivity * this.dateCount
    },
    grandTotal: function () {
      return this.totalPrice + this.surcharge
    },
    canPay: function () {
      return (
        (this.selectedMethod === 'ideal' && this.selectedBank !== null) ||
        (this.selectedMethod !== null && this.selectedMethod !== 'ideal')
      )
    },
    selectedBankName: function () {
      if (!this.selectedBank || !this.banks.length) return ''
      const bank = this.banks.find((b) => b.key === this.selectedBank)
      return bank ? bank.value : ''
    }
  },
  created: function () {
    if (!Object.keys(this.eventDate).length || !this.paymentType) {
      this.$router.replace(`/events/${this.dateId}/signup/${this.securityHash}`)
      return
    }
    this.$store.dispatch(PAYMENT_GET_METHODS)
    this.$store.dispatch(OPTION_GET, 'payments_paypal_fee')
    if (this.paymentType === PURCHASE_CREDITCARD) {
      this.$store.dispatch(EVENT_GET_CREDITCARDS, this.eventDate.eventId)
    }
  },
  methods: {
    handleMethodSelect: function (methodId) {
      this.selectedMethod = methodId
      if (methodId === IDEAL) {
        this.step = 'bank'
      }
    },
    handleBankSelect: function (bankKey) {
      this.selectedBank = bankKey
    },
    resetMethod: function () {
      this.step = 'method'
      this.selectedMethod = null
      this.selectedBank = null
    },
    startPayment: async function () {
      if (!this.termsAccepted) return

      this.paymentLoading = true
      const selectedPaymentMethod = this.selectedMethod
      const issuer = this.selectedBank

      analytics.capture('event_attendance_payment_started', {
        event_id: this.eventDate?.eventId
      })

      if (this.paymentType === PURCHASE_CREDITCARD) {
        const redirectUrl = await this.$store.dispatch(CREDITCARD_PURCHASE, {
          cardId: this.activeCard.id,
          participantId: parseInt(this.authId),
          paymentMethod: selectedPaymentMethod,
          issuer,
          returnUrl: `/events/${this.dateId}/signup/${this.securityHash}`
        })
        if (redirectUrl) {
          window.location.href = redirectUrl
        } else {
          this.showErrorModal = true
        }
      } else if (this.paymentType === SINGLE_PAYMENT) {
        const response = await this.$store.dispatch(EVENT_UPDATE_ATTENDANCE, {
          eventId: this.eventDate.eventId,
          dateId: this.dateId,
          paymentMethod: selectedPaymentMethod,
          issuer,
          securityHash: this.securityHash,
          dates: this.datesToUpdate
        })
        if (response && response.redirectUrl) {
          window.location.href = response.redirectUrl
        } else {
          this.showErrorModal = true
        }
      } else {
        this.showErrorModal = true
      }
      this.paymentLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.inline-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
}
</style>
